/**
 * Coups de pouce — Attendu 661 « Identifier, nommer et caractériser les angles ».
 *
 * Une entrée par `generator_key` (cf. gen_661.js). Une entrée peut être un objet
 * `{ pages }` ou une fonction `(question) => ({ pages })` quand les variantes du
 * format demandent des aides différentes (cf. utils/coupDePouce.js).
 *
 * Règle absolue : l'aide porte sur d'AUTRES lettres et d'AUTRES valeurs que
 * celles que le générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure Ex. 1 : le lexique des six natures ──
// L'exercice demande la nature d'un angle d'apres sa mesure. Une regle ecrite
// serait le tableau des six natures, donc la reponse. La fiche montre les six
// angles DESSINES avec leur plage : l'eleve doit encore y situer sa mesure.
// Les mesures dessinees (45 deg pour l aigu, 130 deg pour l obtus) ne sont pas
// dans les pools du generateur, qui tire 5..89 et 91..179.
const SVG_LEXIQUE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" role="img"
     aria-label="Les six natures d angle dessinees : nul a 0 degre, aigu entre 0 et 90, droit a 90, obtus entre 90 et 180, plat a 180, plein a 360 degres.">
  <line x1="35" y1="72" x2="81" y2="72" stroke="#0f172a" stroke-width="2"/>
  <line x1="35" y1="72" x2="80.6" y2="65.6" stroke="#0f172a" stroke-width="2"/>
  <circle cx="35" cy="72" r="2.5" fill="#0f172a"/>
  <text x="60" y="112" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">nul</text>
  <text x="60" y="128" text-anchor="middle" font-size="11.5" fill="#334155">0&#176;</text>

  <line x1="155" y1="72" x2="201" y2="72" stroke="#0f172a" stroke-width="2"/>
  <line x1="155" y1="72" x2="187.5" y2="39.5" stroke="#0f172a" stroke-width="2"/>
  <path d="M 171 72 A 16 16 0 0 0 166.3 60.7" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="155" cy="72" r="2.5" fill="#0f172a"/>
  <text x="180" y="112" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">aigu</text>
  <text x="180" y="128" text-anchor="middle" font-size="11.5" fill="#334155">entre 0&#176; et 90&#176;</text>

  <line x1="275" y1="72" x2="321" y2="72" stroke="#0f172a" stroke-width="2"/>
  <line x1="275" y1="72" x2="275" y2="26" stroke="#0f172a" stroke-width="2"/>
  <polyline points="287,72 287,60 275,60" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="275" cy="72" r="2.5" fill="#0f172a"/>
  <text x="300" y="112" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">droit</text>
  <text x="300" y="128" text-anchor="middle" font-size="11.5" fill="#334155">90&#176;</text>

  <line x1="45" y1="182" x2="91" y2="182" stroke="#0f172a" stroke-width="2"/>
  <line x1="45" y1="182" x2="15.4" y2="146.8" stroke="#0f172a" stroke-width="2"/>
  <path d="M 61 182 A 16 16 0 0 0 34.7 169.7" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="45" cy="182" r="2.5" fill="#0f172a"/>
  <text x="55" y="222" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">obtus</text>
  <text x="55" y="238" text-anchor="middle" font-size="11.5" fill="#334155">entre 90&#176; et 180&#176;</text>

  <line x1="134" y1="182" x2="226" y2="182" stroke="#0f172a" stroke-width="2"/>
  <path d="M 196 182 A 16 16 0 0 0 164 182" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <circle cx="180" cy="182" r="2.5" fill="#0f172a"/>
  <text x="180" y="222" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">plat</text>
  <text x="180" y="238" text-anchor="middle" font-size="11.5" fill="#334155">180&#176;</text>

  <line x1="300" y1="182" x2="346" y2="182" stroke="#0f172a" stroke-width="2"/>
  <circle cx="300" cy="182" r="18" fill="none" stroke="#F97316" stroke-width="1.8"/>
  <polyline points="313,176 318,182 313,188" fill="none" stroke="#F97316" stroke-width="1.8" stroke-linecap="round"/>
  <circle cx="300" cy="182" r="2.5" fill="#0f172a"/>
  <text x="300" y="222" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">plein</text>
  <text x="300" y="238" text-anchor="middle" font-size="11.5" fill="#334155">360&#176;</text>
</svg>`.trim();

// ── Figure Ex. 2 (variante notation) : le sommet est la lettre du milieu ──
// Le modele C ne parle pas du lexique mais de la NOTATION : la fiche des six
// natures ne lui sert a rien. On montre donc un angle nomme RST, avec son
// sommet S mis en evidence au point ET dans la notation.
const SVG_NOTATION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 232" role="img"
     aria-label="Un angle de sommet S, avec une demi-droite vers R et une vers T. Dans la notation RST, la lettre du milieu S est le sommet de l angle.">
  <line x1="110" y1="140" x2="260" y2="140" stroke="#0f172a" stroke-width="2"/>
  <line x1="110" y1="140" x2="178.8" y2="41.7" stroke="#0f172a" stroke-width="2"/>
  <path d="M 140 140 A 30 30 0 0 0 127.2 115.4" fill="none" stroke="#F97316" stroke-width="2"/>

  <circle cx="110" cy="140" r="5" fill="#F97316"/>
  <circle cx="178.8" cy="41.7" r="4" fill="#0f172a"/>
  <circle cx="260" cy="140" r="4" fill="#0f172a"/>

  <text x="186" y="37" font-size="14" font-weight="700" fill="#0f172a">R</text>
  <text x="100" y="158" text-anchor="end" font-size="14" font-weight="700" fill="#F97316">S</text>
  <text x="268" y="146" font-size="14" font-weight="700" fill="#0f172a">T</text>

  <text x="158" y="196" font-size="20" font-weight="700" fill="#0f172a">R</text>
  <text x="176" y="196" font-size="20" font-weight="700" fill="#F97316">S&#x302;</text>
  <text x="196" y="196" font-size="20" font-weight="700" fill="#0f172a">T</text>
  <line x1="172" y1="204" x2="190" y2="204" stroke="#F97316" stroke-width="2"/>

  <text x="180" y="224" text-anchor="middle" font-size="11.5" font-weight="700" fill="#0f172a">le sommet de l'angle est la lettre du milieu</text>
</svg>`.trim();

/** Lit la phrase du tirage sans supposer que `contenu` est deja parse. */
const phraseDe = (question) => {
    const c = question?.contenu;
    if (!c) return '';
    if (typeof c === 'string') {
        try { return JSON.parse(c)?.phrase || ''; } catch (_e) { return c; }
    }
    return c.phrase || '';
};

// ── Figure Ex. 6 : l'angle plat partage en deux ──
// 40 deg et 140 deg : ni l un ni l autre n est dans MESURES_SUPP, et aucun
// n est produit comme reponse (les reponses valent 180 - x). L exemple ne peut
// donc pas coincider avec le tirage.
const SVG_ANGLE_PLAT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="Une droite passant par A, V et B. La demi-droite VC partage l angle plat AVB en deux angles de 40 et 140 degres.">
  <line x1="50" y1="140" x2="310" y2="140" stroke="#0f172a" stroke-width="2.2"/>
  <line x1="180" y1="140" x2="95.7" y2="69.3" stroke="#378ADD" stroke-width="2.2"/>

  <path d="M 146 140 A 34 34 0 0 1 154 118.1" fill="none" stroke="#F97316" stroke-width="2"/>
  <path d="M 161.6 124.6 A 24 24 0 0 1 204 140" fill="none" stroke="#9333EA" stroke-width="2"/>

  <circle cx="50" cy="140" r="4" fill="#0f172a"/>
  <circle cx="310" cy="140" r="4" fill="#0f172a"/>
  <circle cx="180" cy="140" r="4.5" fill="#0f172a"/>
  <circle cx="95.7" cy="69.3" r="4" fill="#378ADD"/>

  <text x="42" y="159" text-anchor="end" font-size="13" font-weight="700" fill="#0f172a">A</text>
  <text x="318" y="159" font-size="13" font-weight="700" fill="#0f172a">B</text>
  <text x="180" y="162" text-anchor="middle" font-size="13" font-weight="700" fill="#0f172a">V</text>
  <text x="88" y="62" text-anchor="end" font-size="13" font-weight="700" fill="#378ADD">C</text>

  <text x="132" y="128" text-anchor="end" font-size="12" font-weight="700" fill="#F97316">40&#176;</text>
  <text x="200" y="108" font-size="12" font-weight="700" fill="#9333EA">140&#176;</text>

  <text x="180" y="172" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">40&#176; + 140&#176; = 180&#176;</text>
  <text x="180" y="190" text-anchor="middle" font-size="10.5" fill="#334155">deux angles supplémentaires : ensemble ils forment un angle plat</text>
</svg>`.trim();

// -- Figure Ex. 4 : les quatre angles de deux droites sécantes --
// 60 et 120 : aucune des deux valeurs n'est dans MESURES_EX4, et aucune n'est
// produite comme reponse (les réponses valent m ou 180 - m). Les mêmes lettres
// x, y, z, t que l'exercice, pour que l'élève reporte la lecture sans la
// retraduire. Les deux paires d'angles opposés portent chacune leur couleur.
const SVG_SECANTES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 256" role="img"
     aria-label="Deux droites secantes en O formant quatre angles : 60, 120, 60 et 120 degres. Les angles opposés par le sommet sont égaux.">
  <line x1="65" y1="120" x2="295" y2="120" stroke="#0f172a" stroke-width="2"/>
  <line x1="230" y1="33.4" x2="130" y2="206.6" stroke="#0f172a" stroke-width="2"/>

  <path d="M 206 120 A 26 26 0 0 0 193 97.5" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 167 142.5 A 26 26 0 0 1 154 120" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 193 97.5 A 26 26 0 0 0 154 120" fill="none" stroke="#9333EA" stroke-width="2.2"/>
  <path d="M 206 120 A 26 26 0 0 1 167 142.5" fill="none" stroke="#9333EA" stroke-width="2.2"/>

  <circle cx="180" cy="120" r="3" fill="#0f172a"/>

  <text x="219" y="101" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">60&#176;</text>
  <text x="141" y="147" text-anchor="middle" font-size="12" font-weight="700" fill="#F97316">60&#176;</text>
  <text x="158" y="85" text-anchor="middle" font-size="12" font-weight="700" fill="#9333EA">120&#176;</text>
  <text x="203" y="163" text-anchor="middle" font-size="12" font-weight="700" fill="#9333EA">120&#176;</text>

  <text x="303" y="124" font-size="13" font-style="italic" fill="#0f172a">x</text>
  <text x="238" y="30" font-size="13" font-style="italic" fill="#0f172a">y</text>
  <text x="57" y="124" text-anchor="end" font-size="13" font-style="italic" fill="#0f172a">z</text>
  <text x="122" y="220" text-anchor="end" font-size="13" font-style="italic" fill="#0f172a">t</text>
  <text x="188" y="136" font-size="13" fill="#0f172a">O</text>

  <text x="180" y="234" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">deux angles opposés par le sommet sont égaux</text>
  <text x="180" y="249" text-anchor="middle" font-size="11.5" font-weight="700" fill="#9333EA">deux angles côte à côte sur une droite font 180&#176;</text>
</svg>`.trim();

// -- Figure Ex. 5 (figure B) : deux angles adjacents --
// 35 et 45 : aucune des deux n'est dans MESURES_EX5_B ([20..80] de 10 en 10),
// la fiche ne peut donc pas coincider avec le tirage. Le cote commun [Oy) est
// mis en vert, c'est lui qui fait l'adjacence.
const SVG_ADJACENTS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 240" role="img"
     aria-label="Trois demi-droites Ox, Oy et Oz issues de O. L'angle xOy mesure 35 degres, yOz mesure 45 degres, et xOz vaut 80 degres. Le cote Oy est commun aux deux angles.">
  <line x1="110" y1="170" x2="260" y2="170" stroke="#0f172a" stroke-width="2"/>
  <line x1="110" y1="170" x2="136" y2="22.3" stroke="#0f172a" stroke-width="2"/>
  <line x1="110" y1="170" x2="232.9" y2="84" stroke="#1D9E75" stroke-width="3"/>

  <path d="M 144 170 A 34 34 0 0 0 137.8 150.5" fill="none" stroke="#F97316" stroke-width="2.2"/>
  <path d="M 152.6 140.2 A 52 52 0 0 0 119 118.8" fill="none" stroke="#9333EA" stroke-width="2.2"/>

  <circle cx="110" cy="170" r="3.5" fill="#0f172a"/>

  <text x="268" y="174" font-size="13" font-style="italic" fill="#0f172a">x</text>
  <text x="241" y="80" font-size="13" font-style="italic" fill="#1D9E75">y</text>
  <text x="128" y="18" text-anchor="end" font-size="13" font-style="italic" fill="#0f172a">z</text>
  <text x="98" y="186" text-anchor="end" font-size="13" fill="#0f172a">O</text>

  <text x="158" y="156" text-anchor="middle" font-size="11.5" font-weight="700" fill="#F97316">35&#176;</text>
  <text x="147" y="113" text-anchor="middle" font-size="11.5" font-weight="700" fill="#9333EA">45&#176;</text>
  <text x="248" y="104" font-size="10.5" font-weight="700" fill="#1D9E75">côté commun</text>

  <text x="180" y="212" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">35&#176; + 45&#176; = 80&#176;</text>
  <text x="180" y="230" text-anchor="middle" font-size="11" fill="#334155">même sommet O, et le côté [Oy) en commun</text>
</svg>`.trim();

const CP_661 = {
    nature_angle_qcm: {
        pages: [
            { titre: 'La règle', svg: SVG_LEXIQUE },
        ],
    },
    // ENTREE FONCTION : le modele C porte sur la NOTATION, les modeles A et B
    // sur le lexique des natures. Une fiche unique n'aiderait ni l'un ni l'autre.
    // Modèles C, D et E ont chacun leur figure ; A et B, qui portent sur les
    // natures d'angles, partagent le lexique du défaut.
    lexique_angle_trou: {
        variantes: [
            { sousType: 'C', pages: [{ titre: 'La règle', svg: SVG_NOTATION }] },
            { sousType: 'D', pages: [{ titre: 'La règle', svg: SVG_ANGLE_PLAT }] },
            { sousType: 'E', pages: [{ titre: 'La règle', svg: SVG_SECANTES }] },
        ],
        defaut: { pages: [{ titre: 'La règle', svg: SVG_LEXIQUE }] },
    },
    // La notation : le sommet s'écrit au milieu. La règle donne la MÉTHODE sans
    // donner la réponse, qui depend de l'angle marque sur la figure du tirage.
    notation_angle_qcm_svg: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Dans la notation d\'un angle, la lettre du **milieu** est toujours le **sommet**.',
                    'Les deux autres lettres se lisent sur les deux **demi-droites** qui partent de ce sommet.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_NOTATION },
        ],
    },

    supplementaires_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Deux angles sont **supplémentaires** quand la somme de leurs mesures vaut **180°**.',
                    'Donc si l\'un mesure x, l\'autre vaut **180° moins x**.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_ANGLE_PLAT },
        ],
    },
    opposes_adj_saisie: {
        pages: [
            {
                titre: 'La règle',
                lignes: [
                    'Deux angles **opposés par le sommet** ont la **même mesure**.',
                    'Deux angles **adjacents** qui forment une droite sont **supplémentaires** : leurs mesures s\'ajoutent pour faire **180°**.',
                ],
            },
            { titre: 'Un exemple', svg: SVG_SECANTES },
        ],
    },
    // ENTREE FONCTION : le format alterne DEUX figures. A = deux droites
    // secantes (opposes, supplementaires). B = trois demi-droites issues de O
    // (adjacents, cote commun, somme). Une fiche unique n'aiderait ni l'une ni
    // l'autre.
    config_angles_vf: {
        variantes: [
            {
                sousType: 'secantes',
                pages: [
                    { titre: 'La règle', lignes: [
                        'Deux angles **opposés par le sommet** ont la **même mesure**.',
                        'Deux angles **adjacents** qui forment une droite sont **supplémentaires** : ensemble ils font **180°**.',
                    ] },
                    { titre: 'Un exemple', svg: SVG_SECANTES },
                ],
            },
        ],
        defaut: {
            pages: [
                { titre: 'La règle', lignes: [
                    'Deux angles sont **adjacents** quand ils ont le **même sommet**, un **côté commun**, et qu\'ils sont de part et d\'autre de ce côté. Leurs mesures **s\'ajoutent**.',
                    'Ils ne sont **supplémentaires** que si, ensemble, ils forment un angle **plat** de 180°.',
                    'Ils ne sont **opposés par le sommet** que si leurs côtés se prolongent l\'un l\'autre, ce qui demande **deux droites sécantes**.',
                ] },
                { titre: 'Un exemple', svg: SVG_ADJACENTS },
            ],
        },
    },
};

export default CP_661;
